import React, { useState } from 'react';
import { format, addWeeks, addMonths, addQuarters, addYears, isBefore } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, Clock, CheckCircle, ChevronRight, AlertCircle, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Link } from 'react-router-dom';
import api from '../../lib/api';

const frequencyLabel = { weekly: 'Weekly', fortnightly: 'Fortnightly', monthly: 'Monthly', quarterly: 'Quarterly', annually: 'Annually' };

function getNextDate(lastDate, frequency) {
  const d = new Date(lastDate);
  switch (frequency) {
    case 'weekly': return addWeeks(d, 1);
    case 'fortnightly': return addWeeks(d, 2);
    case 'monthly': return addMonths(d, 1);
    case 'quarterly': return addQuarters(d, 1);
    case 'annually': return addYears(d, 1);
    default: return addMonths(d, 1);
  }
}

function getDaysUntil(date) {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.round((new Date(date) - now) / (1000 * 60 * 60 * 24));
}

const fmt = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);

export default function RecurringExpenses({ receipts, onRefetch }) {
  const [confirming, setConfirming] = useState(null);
  const [loading, setLoading] = useState(false);

  const templates = receipts.filter(r => r.is_recurring && r.is_recurring_template);
  const standaloneRecurring = receipts.filter(r => r.is_recurring && !r.is_recurring_template && !r.recurring_parent_id);
  const allTemplates = [...templates, ...standaloneRecurring];

  const upcoming = allTemplates.map(template => {
    const series = receipts.filter(r => r.id === template.id || r.recurring_parent_id === template.id);
    const mostRecent = series.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
    const nextDate = getNextDate(mostRecent.date, template.recurring_frequency || 'monthly');
    if (template.recurring_end_date && isBefore(new Date(template.recurring_end_date), nextDate)) return null;
    return { template, nextDate, daysUntil: getDaysUntil(nextDate), mostRecent };
  }).filter(Boolean).sort((a, b) => a.daysUntil - b.daysUntil);

  const handleConfirm = async (item) => {
    setLoading(true);
    try {
      await api.post('/api/receipts', {
        store_name: item.template.store_name, abn: item.template.abn || '', amount: item.template.amount,
        date: format(item.nextDate, 'yyyy-MM-dd'), payment_method: item.template.payment_method || 'credit_card',
        category: item.template.category || 'other', gst_type: item.template.gst_type || '',
        gst_amount: item.template.gst_amount || 0, tax_deductible: item.template.tax_deductible || false,
        tax_split_percentage: item.template.tax_split_percentage || 100, tax_category: item.template.tax_category || 'none',
        is_business_expense: item.template.is_business_expense || false, client_project: item.template.client_project || '',
        notes: item.template.notes || '', tags: item.template.tags || [], is_recurring: true,
        recurring_frequency: item.template.recurring_frequency || 'monthly', recurring_parent_id: item.template.id,
        is_recurring_template: false, folder: format(item.nextDate, 'MMMM yyyy'),
      });
      onRefetch();
    } finally { setLoading(false); setConfirming(null); }
  };

  if (upcoming.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid="recurring-expenses">
      <div className="p-5 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-violet-100 rounded-lg"><RefreshCw className="w-4 h-4 text-violet-600" /></div>
          <div><h2 className="font-semibold text-slate-900">Recurring Expenses</h2><p className="text-xs text-slate-500">{upcoming.length} upcoming</p></div>
        </div>
        <Link to="/receipts"><Button variant="ghost" size="sm" className="text-xs">Manage <ChevronRight className="w-3 h-3 ml-1" /></Button></Link>
      </div>
      <div className="divide-y divide-slate-50">
        {upcoming.slice(0, 5).map(item => {
          const isOverdue = item.daysUntil < 0;
          const isDueSoon = item.daysUntil >= 0 && item.daysUntil <= 7;
          return (
            <div key={item.template.id} className="flex items-center gap-3 px-5 py-3.5 hover:bg-slate-50 transition-colors">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isOverdue ? 'bg-red-100' : isDueSoon ? 'bg-amber-100' : 'bg-violet-100'}`}>
                <RefreshCw className={`w-3.5 h-3.5 ${isOverdue ? 'text-red-500' : isDueSoon ? 'text-amber-500' : 'text-violet-500'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{item.template.store_name}</p>
                <p className="text-xs text-slate-500">{frequencyLabel[item.template.recurring_frequency] || 'Monthly'}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {isOverdue && <span className="flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full"><AlertCircle className="w-3 h-3" />{Math.abs(item.daysUntil)}d overdue</span>}
                {isDueSoon && !isOverdue && <span className="flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full"><Clock className="w-3 h-3" />{item.daysUntil === 0 ? 'Due today' : `${item.daysUntil}d`}</span>}
                {!isOverdue && !isDueSoon && <span className="text-xs text-slate-400">{format(item.nextDate, 'dd MMM')}</span>}
                <p className="text-sm font-semibold text-slate-900">{fmt(item.template.amount)}</p>
                {(isOverdue || isDueSoon) && <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => setConfirming(item)}><CheckCircle className="w-3 h-3 mr-1" />Add</Button>}
              </div>
            </div>
          );
        })}
      </div>
      <AnimatePresence>
        {confirming && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
              <h3 className="font-semibold text-slate-900 mb-4">Confirm Recurring Expense</h3>
              <div className="bg-slate-50 rounded-xl p-4 mb-5"><p className="font-semibold text-slate-900">{confirming.template.store_name}</p><p className="text-sm text-slate-500">{fmt(confirming.template.amount)} - {format(confirming.nextDate, 'dd MMM yyyy')}</p></div>
              <div className="flex gap-3"><Button variant="outline" className="flex-1" onClick={() => setConfirming(null)} disabled={loading}>Cancel</Button><Button className="flex-1 bg-gradient-to-r from-violet-500 to-purple-500" onClick={() => handleConfirm(confirming)} disabled={loading}>{loading ? 'Saving...' : 'Confirm'}</Button></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
